"use client";

import { useEffect, useMemo } from "react";

// Import new small stores
import { ErrorBoundary } from "@/components/error-boundary";
import { useMapBoundsUpdater } from "@/hooks/use-map-bounds-updater";
import { useMapMarkerZoomUpdater } from "@/hooks/use-map-marker-zoom-updater";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSearchMode } from "@/hooks/use-search-mode";
import { useUrlFilters } from "@/hooks/use-url-filters";
import { mapDefaults } from "@/info/map-defaults";
import { useSearchPageMapData } from "@/providers/search";
import { Map, useMap } from "@vis.gl/react-google-maps";
import { AutoCenterUser } from "./auto-center-user";
import { CenterMarker } from "./center-marker";
import { MapConfigurationError } from "./fallback";
import { Markers } from "./markers";
import { LocationOverlay } from "./nav-overlay";
import { ProjectMarkers } from "./project-markers";
import { UnifiedMapControls } from "./unified-map-controls";
const { center, zoom } = mapDefaults;

interface SearchMapProps {
	mapId?: string;
	showViewSwitcher?: boolean;
	view?: "map" | "list";
	onViewChange?: (v: "map" | "list") => void;
	city?: string;
	mapControlsClassName?: string;
}

// Component to use the bounds fetching hook - must be inside Map context
function MapBoundsHandler() {
	useMapBoundsUpdater();
	useMapMarkerZoomUpdater();
	return null;
}

// Component to sync global state with Map instance
function MapStateSync() {
	const map = useMap();
	const mapTypeId = useSearchPageMapData(state => state.mapTypeId);
	const isFullScreen = useSearchPageMapData(state => state.isFullScreen);
	const setIsFullScreen = useSearchPageMapData(state => state.setIsFullScreen);

	// Sync map type
	useEffect(() => {
		if (map && mapTypeId) {
			map.setMapTypeId(mapTypeId);
		}
	}, [map, mapTypeId]);

	// Sync fullscreen
	useEffect(() => {
		if (!map) return;
		const mapDiv = map.getDiv();

		if (isFullScreen) {
			if (!document.fullscreenElement) {
				mapDiv.requestFullscreen().catch((err: Error) => {
					console.error(`Error attempting to enable fullscreen: ${err.message}`);
				});
			}
		} else {
			if (document.fullscreenElement) {
				document.exitFullscreen();
			}
		}
	}, [map, isFullScreen]);

	// Listen for fullscreen change events (e.g. user presses ESC)
	useEffect(() => {
		const handleFullscreenChange = () => {
			if (!document.fullscreenElement) {
				setIsFullScreen(false);
			}
		};
		document.addEventListener("fullscreenchange", handleFullscreenChange);
		return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
	}, [setIsFullScreen]);

	return null;
}

const MapViewFallback = ({ children }: { children?: React.ReactNode }) => (
	<div className="flex flex-col flex-1 w-full h-full relative bg-gray-100">
		<div className="flex items-center justify-center h-full">
			<div className="text-center p-6">
				<div className="text-gray-800 font-semibold mb-2">Map View Error</div>
				<p className="text-gray-600 text-sm">Unable to load map view. Try switching to list view.</p>
			</div>
		</div>
		{children}
	</div>
);

export function SearchMap({
	mapId = "sellerUnitListMap",
	mapControlsClassName,
	view = "map",
	onViewChange = () => {},
}: SearchMapProps) {
	// Get data from new small stores instead of unified context
	const isMapDataFetching = useSearchPageMapData(state => state.isMapDataFetching);
	const mapCenter = useSearchPageMapData(state => state.mapCenter);
	const mapZoom = useSearchPageMapData(state => state.mapZoom);
	const mode = useSearchPageMapData(state => state.mode);
	const setMode = useSearchPageMapData(state => state.setMode);

	// Get search mode and filters for floating controls
	const { mode: searchMode } = useSearchMode();
	const { filters, setFilters } = useUrlFilters();
	const isMobile = useIsMobile();

	const effectiveCenter = useMemo(() => {
		return mapCenter || center;
	}, [mapCenter]);

	const effectiveZoom = useMemo(() => {
		const finalZoom = mapZoom || zoom;
		return finalZoom;
	}, [mapZoom]);

	const selectedLocation = useMemo(
		() => filters.locations?.find(({ id }) => id === filters.selectedLocationId) || filters.locations?.[0],
		[filters.locations, filters.selectedLocationId],
	);

	useEffect(() => {
		if (selectedLocation?.type === "project") {
			setMode("project");
			if (!filters.selectedLocationId && selectedLocation.id) {
				setFilters({ selectedLocationId: selectedLocation.id });
			}
		}
	}, [selectedLocation, filters.selectedLocationId, setFilters, setMode]);

	// Check if Google Maps API key is available
	const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY;

	if (!apiKey) {
		return (
			<div className="flex flex-col flex-1 w-full relative">
				<div className="map-container flex-grow w-full min-h-[400px]">
					<MapConfigurationError />
				</div>
			</div>
		);
	}
	return (
		<ErrorBoundary fallback={MapViewFallback}>
			<div className="flex flex-col flex-1 w-full h-full relative">
				<div className="map-container flex-grow w-full h-full relative">
					<div className="w-full h-full relative" id="map-container">
						<div className="w-full h-full bg-white focus:outline-none relative overflow-hidden">
							<Map
								defaultCenter={effectiveCenter}
								defaultZoom={effectiveZoom}
								disableDefaultUI={true}
								gestureHandling="greedy"
								id={mapId}
								mapId={mapId}
								reuseMaps
								// Removed key prop to avoid remounting, mapId is stable enough
							>
								<MapBoundsHandler />
								<MapStateSync />
								<AutoCenterUser />
								<CenterMarker />
								{mode === "default" && <Markers />}
								{mode === "project" && <ProjectMarkers />}
								<LocationOverlay isMapDataFetching={isMapDataFetching} />

								{/* Floating Controls (Left Side Mode Switcher) - Hidden as FAB handles it now */}
								{/* <FloatingControls filters={filters} /> */}

								{/* FAB Controls - Only render on Desktop (MobileLayout has its own controls) */}
								{!isMobile && (
									<UnifiedMapControls
										view={view}
										onViewChange={onViewChange}
										showViewToggles={!!onViewChange}
										filters={filters}
										className="absolute bottom-4 right-4"
									/>
								)}
							</Map>
						</div>
					</div>
				</div>
			</div>
		</ErrorBoundary>
	);
}
